import { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type Table as TanStackTable,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { SortableColumnHeader, getColumnSortAria } from "@/components/ui/sortable-column-header";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { Badge } from "@/components/ui/badge";
import {
  QuickFilterCards,
  QuickFilterCardsSkeleton,
  getQuickFilter,
  type QuickFilterGroup,
} from "@/components/QuickFilterCards";
import { RelativeDateCell } from "@/components/RelativeDateCell";
import { SortMenu } from "@/components/SortMenu";
import { formatDateTimeCompact, formatRelativeDateTime } from "../../utils/relative-time";
import { getAggregatorDetailPath } from "../AggregatorAdministrators/config";
import { DEFAULT_PAGE_SIZE, FARMER_SORT_OPTIONS, GENDER_TRANSLATION_KEYS } from "./config";
import { FARMER_QUICK_FILTERS, type FarmerQuickFilterId } from "./quick-filters";
import { type Farmer } from "./types";

interface FarmerColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type FarmerColumnDef = ColumnDef<Farmer, unknown> & {
  meta?: FarmerColumnMeta;
};

const EMPTY_VALUE = "-";

const SKELETON_ROW_COUNT = 8;

const NO_HIDDEN_COLUMNS: string[] = [];
const NO_QUICK_FILTER_COUNTS: Partial<Record<FarmerQuickFilterId, number>> = {};

const QUICK_FILTER_BADGE_VARIANT = {
  healthy: "success",
  warning: "warning",
  attention: "destructive",
} as const satisfies Record<QuickFilterGroup, string>;

const getColumnMeta = (columnDef: ColumnDef<Farmer, unknown>) =>
  columnDef.meta as FarmerColumnMeta | undefined;

const getFarmerFullName = (farmer: Farmer) => farmer.full_name?.trim() || "";

const getAggregatorName = (farmer: Farmer) => farmer.aggregator?.name?.trim() || "";

const getCommodityNames = (farmer: Farmer) =>
  farmer.commodities
    .map(commodity => commodity.name?.trim())
    .filter((name): name is string => Boolean(name))
    .join(", ");

const getIdentification = (farmer: Farmer) => {
  const type = farmer.identification_type?.trim();
  const number = farmer.identification_number?.trim();

  if (!type || !number) {
    return "";
  }

  return `${type}: ${number}`;
};

const getGenderLabel = (farmer: Farmer, t: TFunction) => {
  const gender = farmer.gender?.trim() || "";
  const translationKey = GENDER_TRANSLATION_KEYS[gender];

  return translationKey ? t(translationKey) : gender;
};

const formatValue = (value: string) => value || EMPTY_VALUE;

const formatNumber = (value: number | null | undefined) =>
  typeof value === "number" ? String(value) : EMPTY_VALUE;

const getFarmerRowId = (farmer: Farmer) => String(farmer.id ?? farmer.uuid);

const createColumns = (t: TFunction): FarmerColumnDef[] => [
  {
    id: "date_registered",
    accessorFn: farmer => farmer.date_registered ?? "",
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.farmersModal.columns.dateRegistered")}
      />
    ),
    cell: ({ row }) => {
      const value = row.original.date_registered ?? "";

      return (
        <RelativeDateCell
          relative={formatRelativeDateTime(value, t)}
          absolute={formatDateTimeCompact(value)}
        />
      );
    },
    meta: {
      cellClassName: "w-40",
      headerClassName: "w-40",
    },
  },
  {
    id: "last_profile_update",
    accessorFn: farmer => farmer.last_profile_update ?? "",
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.farmersModal.columns.lastProfileUpdate")}
      />
    ),
    cell: ({ row }) => {
      const value = row.original.last_profile_update ?? "";

      return (
        <RelativeDateCell
          relative={formatRelativeDateTime(value, t)}
          absolute={formatDateTimeCompact(value)}
        />
      );
    },
    meta: {
      cellClassName: "w-40",
      headerClassName: "w-40",
    },
  },
  {
    id: "full_name",
    accessorFn: getFarmerFullName,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.farmersModal.columns.fullName")}
      />
    ),
    cell: ({ row }) => (
      <span className="font-medium text-foreground">
        {formatValue(getFarmerFullName(row.original))}
      </span>
    ),
    meta: {
      cellClassName: "w-48 whitespace-normal leading-5",
      headerClassName: "w-48",
    },
  },
  {
    id: "identification",
    accessorFn: getIdentification,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.farmersModal.columns.identification")}
      />
    ),
    cell: ({ row }) => formatValue(getIdentification(row.original)),
    meta: {
      cellClassName: "w-48 whitespace-normal leading-5 text-muted-foreground",
      headerClassName: "w-48",
    },
  },
  {
    id: "gender",
    accessorFn: farmer => getGenderLabel(farmer, t),
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.farmersModal.columns.gender")}
      />
    ),
    cell: ({ row }) => formatValue(getGenderLabel(row.original, t)),
    meta: {
      cellClassName: "w-24",
      headerClassName: "w-24",
    },
  },
  {
    id: "total_household_members",
    accessorFn: farmer => farmer.total_household_members ?? -1,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        align="center"
        label={t("home.manager.overview.farmersModal.columns.householdMembers")}
      />
    ),
    cell: ({ row }) => formatNumber(row.original.total_household_members),
    meta: {
      cellClassName: "w-32 text-center tabular-nums",
      headerClassName: "w-32",
    },
  },
  {
    id: "commodities",
    accessorFn: getCommodityNames,
    enableSorting: false,
    header: () => t("home.manager.overview.farmersModal.columns.commodities"),
    cell: ({ row }) => formatValue(getCommodityNames(row.original)),
    meta: {
      cellClassName: "w-32 whitespace-normal leading-5",
      headerClassName: "w-32",
    },
  },
  {
    id: "total_deposits",
    accessorFn: farmer => farmer.total_deposits ?? 0,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        align="center"
        label={t("home.manager.overview.farmersModal.columns.totalDeposits")}
      />
    ),
    cell: ({ row }) => formatNumber(row.original.total_deposits),
    meta: {
      cellClassName: "w-28 text-center tabular-nums",
      headerClassName: "w-28",
    },
  },
  {
    id: "total_events",
    accessorFn: farmer => farmer.total_events ?? 0,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        align="center"
        label={t("home.manager.overview.farmersModal.columns.totalEvents")}
      />
    ),
    cell: ({ row }) => formatNumber(row.original.total_events),
    meta: {
      cellClassName: "w-28 text-center tabular-nums",
      headerClassName: "w-28",
    },
  },
  {
    id: "aggregator",
    accessorFn: getAggregatorName,
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.farmersModal.columns.aggregator")}
      />
    ),
    cell: ({ row }) => {
      const aggregatorId = row.original.aggregator?.id;
      const aggregatorName = getAggregatorName(row.original);

      if (aggregatorId === undefined || aggregatorId === null || !aggregatorName) {
        return formatValue(aggregatorName);
      }

      return (
        <Link
          to={getAggregatorDetailPath(aggregatorId)}
          className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          {aggregatorName}
        </Link>
      );
    },
    meta: {
      cellClassName: "w-56 whitespace-normal leading-5",
      headerClassName: "w-56",
    },
  },
  {
    id: "admin_area",
    accessorFn: farmer => farmer.admin_area?.trim() ?? "",
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("home.manager.overview.farmersModal.columns.adminArea")}
      />
    ),
    cell: ({ row }) => formatValue(row.original.admin_area?.trim() ?? ""),
    meta: {
      cellClassName: "w-56 whitespace-normal leading-5 text-muted-foreground",
      headerClassName: "w-56",
    },
  },
];

const FarmersDataTable = ({
  emptyMessage,
  isLoading,
  table,
}: {
  emptyMessage: string;
  isLoading: boolean;
  table: TanStackTable<Farmer>;
}) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                className={getColumnMeta(header.column.columnDef)?.headerClassName}
                aria-sort={getColumnSortAria(header.column)}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading
          ? Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                {table.getVisibleLeafColumns().map(column => (
                  <TableCell
                    key={column.id}
                    className={getColumnMeta(column.columnDef)?.cellClassName}
                  >
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
        {!isLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length
          ? rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
  );
};

interface FarmersToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sorting: SortingState;
  onSortColumnChange: (columnId: string) => void;
  onSortDirectionChange: (direction: string) => void;
  quickFilterLabel?: string;
  quickFilterGroup?: QuickFilterGroup;
  onClearQuickFilter: () => void;
  hiddenColumns: string[];
}

const FarmersToolbar = ({
  searchTerm,
  onSearchChange,
  sorting,
  onSortColumnChange,
  onSortDirectionChange,
  quickFilterLabel,
  quickFilterGroup,
  onClearQuickFilter,
  hiddenColumns,
}: FarmersToolbarProps) => {
  const { t } = useTranslation();
  const sortOptions = FARMER_SORT_OPTIONS.filter(
    sortOption => !hiddenColumns.includes(sortOption.id)
  );
  const selectedSort = sorting[0];
  const selectedSortOption = FARMER_SORT_OPTIONS.find(
    sortOption => sortOption.id === selectedSort?.id
  );
  const selectedSortColumn = selectedSortOption?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <InputGroup className="w-full max-w-xs sm:w-80">
        <InputGroupAddon>
          <Search className="size-4" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          value={searchTerm}
          className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          placeholder={t("home.manager.overview.farmersModal.searchPlaceholder")}
          aria-label={t("home.manager.overview.farmersModal.searchPlaceholder")}
          onChange={event => onSearchChange(event.target.value)}
        />
        {searchTerm ? (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={t("home.manager.overview.farmersModal.clearSearch")}
              onClick={() => onSearchChange("")}
            >
              <X className="size-3.5" aria-hidden="true" />
            </InputGroupButton>
          </InputGroupAddon>
        ) : null}
      </InputGroup>

      <SortMenu
        options={sortOptions}
        sortColumn={selectedSortColumn}
        sortDirection={selectedSortDirection}
        onSortColumnChange={onSortColumnChange}
        onSortDirectionChange={onSortDirectionChange}
      />

      {quickFilterLabel ? (
        <Badge
          variant={quickFilterGroup ? QUICK_FILTER_BADGE_VARIANT[quickFilterGroup] : "default"}
          className="gap-1.5 py-1 pr-1 pl-2.5"
        >
          {quickFilterLabel}
          <button
            type="button"
            aria-label={t("home.manager.overview.farmersModal.quickFilters.clear")}
            onClick={onClearQuickFilter}
            className="inline-flex cursor-pointer items-center justify-center rounded-full p-0.5 hover:bg-neutral-alpha-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X aria-hidden="true" size={16} />
          </button>
        </Badge>
      ) : null}
    </div>
  );
};

interface FarmersTableProps {
  farmers: Farmer[];
  totalCount: number;
  isFetchingFarmers: boolean;
  quickFilterCounts?: Partial<Record<FarmerQuickFilterId, number>>;
  hiddenColumns?: string[];
  page: number;
  onPageChange: (page: number) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  quickFilter: FarmerQuickFilterId | null;
  onQuickFilterChange: (quickFilter: FarmerQuickFilterId | null) => void;
  showQuickFilters?: boolean;
}

export function FarmersTable({
  farmers,
  totalCount,
  isFetchingFarmers,
  quickFilterCounts = NO_QUICK_FILTER_COUNTS,
  hiddenColumns = NO_HIDDEN_COLUMNS,
  page,
  onPageChange,
  searchTerm,
  onSearchChange,
  sorting,
  onSortingChange,
  quickFilter,
  onQuickFilterChange,
  showQuickFilters = false,
}: FarmersTableProps) {
  const { t } = useTranslation();

  const columns = useMemo(() => createColumns(t), [t]);

  const columnVisibility = useMemo(
    () => Object.fromEntries(hiddenColumns.map(columnId => [columnId, false])),
    [hiddenColumns]
  );

  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: page - 1, pageSize: DEFAULT_PAGE_SIZE }),
    [page]
  );

  const activeQuickFilter = getQuickFilter(FARMER_QUICK_FILTERS, quickFilter);
  const quickFilterLabel = activeQuickFilter ? t(activeQuickFilter.labelKey) : undefined;

  const onPaginationChange: OnChangeFn<PaginationState> = updater => {
    const next = typeof updater === "function" ? updater(pagination) : updater;

    onPageChange(next.pageIndex + 1);
  };

  const onTableSortingChange: OnChangeFn<SortingState> = updater => {
    onSortingChange(typeof updater === "function" ? updater(sorting) : updater);
  };

  const table = useReactTable({
    columns,
    data: farmers,
    enableMultiSort: false,
    enableSortingRemoval: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getFarmerRowId,
    onPaginationChange,
    onSortingChange: onTableSortingChange,
    state: {
      columnVisibility,
      pagination,
      sorting,
    },
  });

  const onSortColumnChange = (columnId: string) => {
    onSortingChange([{ id: columnId, desc: sorting[0]?.desc ?? false }]);
  };

  const onSortDirectionChange = (direction: string) => {
    onSortingChange([
      { id: sorting[0]?.id ?? FARMER_SORT_OPTIONS[0].id, desc: direction === "desc" },
    ]);
  };

  return (
    <div className="flex flex-col gap-4">
      {showQuickFilters && isFetchingFarmers ? (
        <QuickFilterCardsSkeleton filters={FARMER_QUICK_FILTERS} />
      ) : null}
      {showQuickFilters && !isFetchingFarmers ? (
        <QuickFilterCards
          counts={quickFilterCounts}
          filters={FARMER_QUICK_FILTERS}
          ariaLabel={t("home.manager.overview.farmersModal.quickFilters.ariaLabel")}
          value={quickFilter}
          onChange={onQuickFilterChange}
        />
      ) : null}
      <FarmersToolbar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        sorting={sorting}
        onSortColumnChange={onSortColumnChange}
        onSortDirectionChange={onSortDirectionChange}
        quickFilterLabel={quickFilterLabel}
        quickFilterGroup={activeQuickFilter?.group}
        onClearQuickFilter={() => onQuickFilterChange(null)}
        hiddenColumns={hiddenColumns}
      />
      <div className="overflow-hidden rounded-md border border-border bg-background">
        <FarmersDataTable
          emptyMessage={t("home.manager.overview.farmersModal.empty")}
          isLoading={isFetchingFarmers}
          table={table}
        />
        {totalCount > 0 ? <DataTablePagination table={table} /> : null}
      </div>
    </div>
  );
}
